import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { purchaseCreditRefill } from '../services/iapService';
import { sanitizeErrorMessage } from '../utils/safeJson';
import useDynamicPrices from '../hooks/useDynamicPrices';
import { rlog } from '../utils/railwayLogger';

// Credit refill packs - maps to backend package_ids
const CREDIT_PACKS = [
  { id: 'credits_500', credits: 500, questions: 5, tag: null },
  { id: 'credits_1000', credits: 1000, questions: 10, tag: 'POPULAR' },
  { id: 'credits_1500', credits: 1500, questions: 15, tag: 'BEST VALUE' },
];

const CreditsRefillModal = ({ isOpen, onClose, userId, currentCredits, onCreditsUpdated }) => {
  const [selectedPack, setSelectedPack] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [addedCredits, setAddedCredits] = useState(0);
  const { getDisplayPrice } = useDynamicPrices();

  const handlePurchase = async (pack) => {
    if (!userId) {
      toast.error('Please login first');
      return;
    }

    setSelectedPack(pack.id);
    setProcessing(true);
    rlog('credits_refill_start', {
      pack_id: pack.id,
      pack_credits: pack.credits,
      current_credits: currentCredits,
      user_id: userId,
    });

    try {
      const result = await purchaseCreditRefill(pack.id, userId);
      rlog('credits_refill_result', {
        pack_id: pack.id,
        success: !!result.success,
        cancelled: !!result.cancelled,
        redirected: !!result.redirected,
        credits_added: result.credits_added,
        message: (result.message || '').slice(0, 120),
      });

      if (result.redirected) {
        toast.info('Complete payment on taraanaya.ai');
        setProcessing(false);
        setSelectedPack(null);
        return;
      }

      if (result.domain_blocked) {
        toast.error(result.message || 'Payments only work on taraanaya.ai or the app', { duration: 6000 });
        setProcessing(false);
        setSelectedPack(null);
        return;
      }

      if (result.success) {
        const added = result.credits_added || pack.credits;
        setAddedCredits(added);
        setShowSuccess(true);
        
        if (onCreditsUpdated) {
          const newBalance = (currentCredits || 0) + added;
          onCreditsUpdated(newBalance);
        }
        
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 1500);
      } else if (result.cancelled) {
        // silent dismiss
      } else {
        toast.error(sanitizeErrorMessage(result.message, 'Payment failed. Please try again.'));
      }
    } catch (error) {
      if (error.message !== 'Payment cancelled') {
        toast.error('Payment failed. Please try again.');
      }
    } finally {
      setProcessing(false);
      setSelectedPack(null);
    }
  };

  if (!isOpen) return null;

  // Taraanaya blue theme colors
  const blueAccent = '#74B3FF';
  const blueDim = 'rgba(116, 179, 255, 0.15)';
  const blueBorder = 'rgba(116, 179, 255, 0.25)';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end justify-center"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 350 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
          style={{
            background: 'linear-gradient(180deg, #0f1825 0%, #0a0e17 100%)',
            borderTopLeftRadius: '28px',
            borderTopRightRadius: '28px',
            border: `1px solid ${blueBorder}`,
            borderBottom: 'none',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          {/* Success Overlay */}
          <AnimatePresence>
            {showSuccess && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-10 flex items-center justify-center rounded-t-3xl"
                style={{ background: 'rgba(10,14,23,0.98)' }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.1 }}
                    className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(116,179,255,0.15)', border: `2px solid ${blueAccent}` }}
                  >
                    <Check className="w-7 h-7" style={{ color: blueAccent }} />
                  </motion.div>
                  <p style={{ fontSize: '20px', fontWeight: '600', color: blueAccent }}>+{addedCredits} Credits</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <h2 style={{ fontSize: '17px', fontWeight: '600', color: 'rgba(255,255,255,0.95)', letterSpacing: '-0.3px' }}>
              Refill Credits
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              <X className="w-4 h-4 text-white/40" />
            </button>
          </div>

          {/* Current Balance */}
          <div className="px-6 pb-5">
            <div 
              className="flex items-center justify-between py-3 px-4 rounded-xl"
              style={{ background: blueDim, border: `1px solid ${blueBorder}` }}
            >
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>Current Balance</span>
              <span style={{ fontSize: '18px', fontWeight: '700', color: blueAccent }}>{currentCredits}</span>
            </div>
          </div>

          {/* Credit Packs */}
          <div className="px-6 pb-5 space-y-3">
            {CREDIT_PACKS.map((pack) => (
              <motion.button
                key={pack.id}
                whileTap={{ scale: 0.98 }}
                onClick={() => handlePurchase(pack)}
                disabled={processing}
                className="w-full relative"
              >
                <div
                  className="flex items-center justify-between p-4 rounded-2xl transition-all"
                  style={{
                    background: pack.tag === 'POPULAR' ? blueDim : 'rgba(255,255,255,0.02)',
                    border: pack.tag === 'POPULAR' 
                      ? `1.5px solid ${blueAccent}` 
                      : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {/* Tag */}
                  {pack.tag && (
                    <div
                      className="absolute -top-2 left-4 px-2 py-0.5 rounded-full"
                      style={{ background: blueAccent }}
                    >
                      <span style={{ fontSize: '9px', fontWeight: '700', color: '#0a0e17', letterSpacing: '0.5px' }}>
                        {pack.tag}
                      </span>
                    </div>
                  )}

                  {/* Left - Credits & Questions */}
                  <div className="text-left">
                    <p style={{ 
                      fontSize: '22px', 
                      fontWeight: '700', 
                      color: pack.tag === 'POPULAR' ? blueAccent : 'rgba(255,255,255,0.9)',
                      letterSpacing: '-0.5px'
                    }}>
                      {pack.credits.toLocaleString()}
                    </p>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                      {pack.questions} questions
                    </p>
                  </div>

                  {/* Right - Real price from native store (iOS/Android) or Razorpay backend (web) */}
                  <div className="text-right">
                    {processing && selectedPack === pack.id ? (
                      <div 
                        className="w-5 h-5 rounded-full animate-spin"
                        style={{ border: `2px solid ${blueBorder}`, borderTopColor: blueAccent }}
                      />
                    ) : (
                      <p style={{ 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        color: 'rgba(255,255,255,0.85)',
                        letterSpacing: '-0.3px'
                      }}>
                        {getDisplayPrice(pack.id) || '—'}
                      </p>
                    )}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
              100 credits = 1 question · Credits never expire
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreditsRefillModal;
